import React from 'react';
import { Box, Typography, Grid } from '@mui/material';

interface SubcategorySlot {
  label: string;
  imageSlug: string;
}

interface CategoryCardDef {
  name: string;
  slug: string;
  subcategories: SubcategorySlot[];
}

const CATEGORY_CARDS: CategoryCardDef[] = [
  {
    name: 'Connectivity',
    slug: 'connectivity',
    subcategories: [
      { label: 'Fiber Internet', imageSlug: 'connectivity-fiber' },
      { label: 'Ethernet & WAN', imageSlug: 'connectivity-ethernet' },
      { label: 'Wireless & 4G/5G', imageSlug: 'connectivity-wireless' },
      { label: 'Cloud Connect', imageSlug: 'connectivity-cloudconnect' },
    ],
  },
  {
    name: 'Voice',
    slug: 'voice',
    subcategories: [
      { label: 'UCaaS / VoIP', imageSlug: 'voice-ucaas' },
      { label: 'SIP Trunking', imageSlug: 'voice-sip' },
      { label: 'Business Voice', imageSlug: 'voice-business' },
      { label: 'Unified Communications', imageSlug: 'voice-uc' },
    ],
  },
  {
    name: 'Cloud',
    slug: 'cloud',
    subcategories: [
      { label: 'Cloud Backup & DR', imageSlug: 'cloud-backup' },
      { label: 'Business Intelligence', imageSlug: 'cloud-bi' },
      { label: 'Team & Collaboration', imageSlug: 'cloud-collab' },
      { label: 'Guest Experience', imageSlug: 'cloud-guest' },
    ],
  },
  {
    name: 'Security',
    slug: 'security',
    subcategories: [
      { label: 'Compliance & Assessment', imageSlug: 'security-compliance' },
      { label: 'Enterprise Security', imageSlug: 'security-enterprise' },
      { label: 'Managed Firewall', imageSlug: 'security-firewall' },
      { label: 'DDoS Protection', imageSlug: 'security-ddos' },
    ],
  },
  {
    name: 'Managed Services',
    slug: 'managed-services',
    subcategories: [
      { label: 'Managed Network', imageSlug: 'managed-network' },
      { label: 'Managed Router', imageSlug: 'managed-router' },
      { label: 'IT / Helpdesk', imageSlug: 'managed-helpdesk' },
      { label: 'Network Audit', imageSlug: 'managed-audit' },
    ],
  },
  {
    name: 'Consulting',
    slug: 'consulting',
    subcategories: [
      { label: 'AI Consulting', imageSlug: 'consulting-ai' },
      { label: 'Financial Advisory', imageSlug: 'consulting-financial' },
      { label: 'Strategy & Planning', imageSlug: 'consulting-strategy' },
      { label: 'Brand & Digital', imageSlug: 'consulting-brand' },
    ],
  },
  {
    name: 'TV & Media',
    slug: 'tv-media',
    subcategories: [
      { label: 'Business TV', imageSlug: 'tvmedia-businesstv' },
      { label: 'Set Back Box', imageSlug: 'tvmedia-setbox' },
      { label: 'Digital Signage', imageSlug: 'tvmedia-signage' },
      { label: 'Video Production', imageSlug: 'tvmedia-video' },
    ],
  },
  {
    name: 'Marketing',
    slug: 'marketing',
    subcategories: [
      { label: 'Brand & Web Design', imageSlug: 'marketing-brand' },
      { label: 'Digital Marketing', imageSlug: 'marketing-digital' },
      { label: 'Content & Creative', imageSlug: 'marketing-content' },
      { label: 'Growth Strategy', imageSlug: 'marketing-growth' },
    ],
  },
];

const IMAGE_BASE = '/assets/marketplace/subcategories';

interface CategoryCardGridProps {
  onSelectCategory: (categoryName: string) => void;
  /** Optional accent color from catalog branding */
  accentColor?: string;
}

export const CategoryCardGrid: React.FC<CategoryCardGridProps> = ({
  onSelectCategory,
  accentColor = '#1976d2',
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Row 1: first 4 categories */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {CATEGORY_CARDS.slice(0, 4).map((cat) => (
          <Grid item xs={6} sm={3} key={cat.slug}>
            <CategoryCard cat={cat} onSelect={onSelectCategory} accentColor={accentColor} />
          </Grid>
        ))}
      </Grid>

      {/* Row 2: next 4 categories */}
      <Grid container spacing={2}>
        {CATEGORY_CARDS.slice(4, 8).map((cat) => (
          <Grid item xs={6} sm={3} key={cat.slug}>
            <CategoryCard cat={cat} onSelect={onSelectCategory} accentColor={accentColor} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

interface CategoryCardProps {
  cat: CategoryCardDef;
  onSelect: (name: string) => void;
  accentColor: string;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ cat, onSelect, accentColor }) => {
  return (
    <Box
      onClick={() => onSelect(cat.name)}
      sx={{
        borderStyle: 'solid',
        borderWidth: { xs: '1px', md: '2px' },
        borderColor: { xs: 'divider', md: 'rgba(0,0,0,0.38)' },
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        bgcolor: 'background.paper',
        transition: 'box-shadow 0.18s, border-color 0.18s',
        '&:hover': {
          boxShadow: 4,
          borderColor: accentColor,
        },
      }}
    >
      {/* Category name header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottomStyle: 'solid',
          borderBottomWidth: { xs: '1px', md: '2px' },
          borderColor: { xs: 'divider', md: 'rgba(0,0,0,0.30)' },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, fontSize: { xs: '0.85rem', md: '1.05rem' }, color: accentColor }}
        >
          {cat.name}
        </Typography>
      </Box>

      {/* 2×2 subcategory image grid */}
      <Grid container sx={{ p: 0.5 }} spacing={0.5}>
        {cat.subcategories.map((sub) => (
          <Grid item xs={6} key={sub.imageSlug}>
            <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
              <Box
                component="img"
                src={`${IMAGE_BASE}/${sub.imageSlug}.jpg`}
                alt={sub.label}
                sx={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              {/* Subcategory label overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                  px: { xs: 0.5, md: 0.75 },
                  py: { xs: 0.4, md: 0.6 },
                }}
              >
                <Typography
                  sx={{
                    color: '#fff',
                    fontSize: { xs: '0.6rem', md: '0.8rem' },
                    fontWeight: 600,
                    lineHeight: 1.2,
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                  }}
                >
                  {sub.label}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* "and more" footer */}
      <Box sx={{ px: 1.5, py: 0.75, textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: '0.72rem',
            color: accentColor,
            fontWeight: 600,
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          and more →
        </Typography>
      </Box>
    </Box>
  );
};

export default CategoryCardGrid;
